// Each group of a reading confirmed on its own, and un-confirmed by any change to it (TGCLOUD-1868,
// plan D3; hazard 1868-H1).
//
//   const groups = useGroupConfirmations({ scopeKey, groups, signatureOf, onConfirmed });
//   groups.confirm(key)          one group, and only that one, confirmed now
//   groups.isConfirmed(key)      confirmed, and the group is as it was when it was confirmed
//   groups.confirmedAt(key)      when (an ISO instant), or null
//   groups.confirmations         labReportApplication.confirmations: [{ group, at }]
//   groups.count, groups.total
//
// `scopeKey` is the review being checked: another review starts with nothing confirmed.
// `signatureOf(group)` is what the form holds for the group now (verificationGroups.groupSignature).
// A confirmation is kept with the signature it was made against and counts only while that is
// still the group's: an edit to any of its values un-confirms it at once, and it does not come
// back when the value is put back. `onConfirmed(key)` is the telemetry's groupConfirmed.
//
// THERE IS NO WAY TO CONFIRM MORE THAN ONE GROUP. `confirm` takes one key; nothing here takes a
// list.
import React from 'react';
import { confirmationsOf } from './verificationGroups';

const EMPTY = Object.freeze({});

export default function useGroupConfirmations({
  scopeKey = null,
  groups = [],
  signatureOf,
  onConfirmed
} = {}) {
  const [state, setState] = React.useState({ scopeKey, confirmed: EMPTY });
  const confirmed = state.scopeKey === scopeKey ? state.confirmed : EMPTY;

  const signatures = new Map(
    (Array.isArray(groups) ? groups : []).map((g) => [
      g.key,
      signatureOf ? signatureOf(g) : ''
    ])
  );
  const holds = (key) =>
    !!confirmed[key] &&
    signatures.has(key) &&
    confirmed[key].signature === signatures.get(key);

  // A confirmation whose group has changed since is dropped, so putting the value back does not
  // bring it back: the group is checked again.
  const stale = Object.keys(confirmed).filter((key) => !holds(key));
  const staleKey = stale.join('\u0000');
  React.useEffect(() => {
    if (!staleKey) return;
    const drop = new Set(staleKey.split('\u0000'));
    setState((prev) => {
      if (prev.scopeKey !== scopeKey) return prev;
      const next = { ...prev.confirmed };
      for (const key of drop) delete next[key];
      return { scopeKey, confirmed: next };
    });
  }, [staleKey, scopeKey]);

  const onConfirmedRef = React.useRef(onConfirmed);
  onConfirmedRef.current = onConfirmed;

  const confirm = (key) => {
    if (!signatures.has(key)) return;
    const signature = signatures.get(key);
    const at = new Date().toISOString();
    setState((prev) => ({
      scopeKey,
      confirmed: {
        ...(prev.scopeKey === scopeKey ? prev.confirmed : {}),
        [key]: { at, signature }
      }
    }));
    onConfirmedRef.current?.(key);
  };

  const confirmedAt = (key) => (holds(key) ? confirmed[key].at : null);
  const list = Array.isArray(groups) ? groups : [];
  const confirmations = confirmationsOf(list, confirmedAt);
  return {
    confirm,
    isConfirmed: holds,
    confirmedAt,
    confirmations,
    count: confirmations.length,
    total: list.length
  };
}
